import { convertNumber } from '@/helpers/utils/functionality-utils';
import type { MyAlarm } from '@/types/user/alarm-types';
import type { MyNotification } from '@/types/user/notification-types';
import type { MyTask, TaskAssignedByMe } from '@/types/user/task-types';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { isThisWeek, isToday } from 'date-fns';

// assigned-by-me : ABM

export interface StaffDashboardStateInterface {
  pendingTasksState: {
    pg: number;
    pendingTasks: MyTask[];
    todaysTaskCount: number;
    checkedTasks: Record<number, boolean>;
    showLoadMore: boolean;
  };
  completedTasksState: {
    completedTasks: MyTask[];
    pg: number;
    checkedTasks: Record<number, boolean>;
    showLoadMore: boolean;
  };
  pendingTasksABMState: {
    pg: number;
    pendingTasks: TaskAssignedByMe[];
    showLoadMore: boolean;
  };
  completedTasksABMState: {
    completedTasks: TaskAssignedByMe[];
    pg: number;
    showLoadMore: boolean;
  };
  myNotificationsState: {
    notifications: MyNotification[];
    pg: number;
    showLoadMore: boolean;
    notificationThisWeek: number;
  };
  myAlarmsState: {
    alarms: MyAlarm[];
    pg: number;
    showLoadMore: boolean;
    todaysAlarmsCount: number;
  };
}

export const staffDMyTaskInitialState: StaffDashboardStateInterface = {
  pendingTasksState: {
    pendingTasks: [],
    pg: 1,
    todaysTaskCount: 0,
    checkedTasks: {},
    showLoadMore: true,
  },
  completedTasksState: {
    completedTasks: [],
    pg: 1,
    checkedTasks: {},
    showLoadMore: true,
  },
  pendingTasksABMState: {
    pendingTasks: [],
    pg: 1,
    showLoadMore: true,
  },
  completedTasksABMState: {
    completedTasks: [],
    pg: 1,
    showLoadMore: true,
  },
  myNotificationsState: {
    notifications: [],
    pg: 1,
    showLoadMore: true,
    notificationThisWeek: 0,
  },
  myAlarmsState: {
    alarms: [],
    pg: 1,
    showLoadMore: true,
    todaysAlarmsCount: 0,
  },
};

export const staffDashboardSlice = createSlice({
  name: 'staffDashboard',
  initialState: staffDMyTaskInitialState,
  reducers: {
    // my-pending-tasks reducers
    refreshPendingTasks: (state, action: PayloadAction<MyTask[]>) => {
      const newTasks = action.payload;
      state.pendingTasksState.pendingTasks = newTasks;
      state.pendingTasksState.pg = 2;
      if (convertNumber(newTasks?.length) >= 10) {
        state.pendingTasksState.showLoadMore = true;
      } else {
        state.pendingTasksState.showLoadMore = false;
      }
      const checkedTasks: Record<number, boolean> = {};
      newTasks.forEach(
        (task) =>
          (checkedTasks[task.task_id] =
            task.status === 'completed' ? true : false),
      );
      state.pendingTasksState.checkedTasks = checkedTasks;
    },
    setPendingTasks: (state, action: PayloadAction<MyTask[]>) => {
      const newTasks = action.payload;
      const previousTasks = state.pendingTasksState.pendingTasks
        .filter((task) => !state.pendingTasksState.checkedTasks[task.task_id])
        .map((task) => ({
          ...task,
          isNew: false,
        }));

      const previousTasksIds = previousTasks.map((task) => task.task_id);

      const newTasksFiltered = newTasks
        // filtering tasks present in previous task
        .filter((task) => !previousTasksIds.includes(task.task_id))
        // adding isNew key
        .map((task) => ({ ...task, isNew: true }));

      const allTasks = [...previousTasks, ...newTasksFiltered];

      state.pendingTasksState.pendingTasks = allTasks;
      state.pendingTasksState.pg += 1;
      state.pendingTasksState.todaysTaskCount = allTasks.filter((task) =>
        isToday(new Date(task.assigned_on)),
      ).length;

      const checkedTasks: Record<number, boolean> = {};
      allTasks.forEach((task) => (checkedTasks[task.task_id] = false));
      state.pendingTasksState.checkedTasks = {
        ...checkedTasks,
        ...state.pendingTasksState.checkedTasks,
      };
      if (convertNumber(newTasks?.length) < 10) {
        state.pendingTasksState.showLoadMore = false;
      }
    },
    setCheckedPending: (
      state,
      action: PayloadAction<{ id: number; checked: boolean }>,
    ) => {
      const { id, checked } = action.payload;
      state.pendingTasksState.checkedTasks[id] = checked;
      state.completedTasksState.checkedTasks[id] = checked;
    },
    updatePendingTasks: (state, action: PayloadAction<MyTask[]>) => {
      const newTasks = (action.payload || []).map((task) => ({
        ...task,
        isNew: true,
      }));
      const oldTasks = state.pendingTasksState.pendingTasks;
      const oldTaskIds = oldTasks.map((task) => task.task_id);
      const filteredNewTasks = newTasks.filter(
        (task) => !oldTaskIds.includes(task.task_id),
      );
      const allTasks = [...filteredNewTasks, ...oldTasks];
      state.pendingTasksState.pendingTasks = allTasks;
      state.pendingTasksState.todaysTaskCount = allTasks.filter((task) =>
        isToday(new Date(task.assigned_on)),
      ).length;
    },

    // my-completed-tasks reducers
    refreshCompletedTasks: (state, action: PayloadAction<MyTask[]>) => {
      const newTasks = action.payload;

      state.completedTasksState.completedTasks = newTasks;
      state.completedTasksState.pg = 2;
      if (convertNumber(newTasks?.length) >= 10) {
        state.completedTasksState.showLoadMore = true;
      } else {
        state.completedTasksState.showLoadMore = false;
      }
      const checkedTasks: Record<number, boolean> = {};
      newTasks.forEach(
        (task) =>
          (checkedTasks[task.task_id] =
            task.status === 'completed' ? true : false),
      );
      state.completedTasksState.checkedTasks = checkedTasks;
    },
    setCompletedTasks: (state, action: PayloadAction<MyTask[]>) => {
      const newTasks = action.payload;
      const previousTasks = state.completedTasksState.completedTasks.map(
        (task) => ({
          ...task,
          isNew: false,
        }),
      );
      const previousTasksIds = previousTasks.map((task) => task.task_id);

      const newTasksFiltered = newTasks
        // filtering tasks present in previous task
        .filter((task) => !previousTasksIds.includes(task.task_id))
        // adding isNew key
        .map((task) => ({ ...task, isNew: true }));

      const allTasks = [...previousTasks, ...newTasksFiltered];

      state.completedTasksState.completedTasks = allTasks;
      state.completedTasksState.pg += 1;

      const checkedTasks: Record<number, boolean> = {};
      allTasks.forEach(
        (task) =>
          (checkedTasks[task.task_id] =
            task.status?.toLowerCase() === 'completed' ? true : false),
      );
      state.completedTasksState.checkedTasks = {
        ...checkedTasks,
        ...state.completedTasksState.checkedTasks,
      };
      if (convertNumber(newTasks?.length) < 10) {
        state.completedTasksState.showLoadMore = false;
      }
    },
    setCheckedCompleted: (
      state,
      action: PayloadAction<{ id: number; checked: boolean }>,
    ) => {
      const { id, checked } = action.payload;
      state.completedTasksState.checkedTasks[id] = checked;
      state.pendingTasksState.checkedTasks[id] = checked;
    },
    updateCompletedTasks: (state, action: PayloadAction<MyTask[]>) => {
      const newTasks = (action.payload || []).map((task) => ({
        ...task,
        isNew: true,
      }));
      const oldTasks = state.completedTasksState.completedTasks;
      const oldTaskIds = oldTasks.map((task) => task.task_id);
      const filteredNewTasks = newTasks.filter(
        (task) => !oldTaskIds.includes(task.task_id),
      );
      const allTasks = [...filteredNewTasks, ...oldTasks];
      state.completedTasksState.completedTasks = allTasks;
    },

    // common reducers
    setAsRead(state, action: PayloadAction<number[]>) {
      state.pendingTasksState.pendingTasks.map((task) => {
        if (action.payload.includes(task.task_id)) {
          task.isNew = false;
        }
      });
      state.completedTasksState.completedTasks.map((task) => {
        if (action.payload.includes(task.task_id)) {
          task.isNew = false;
        }
      });
    },
    setAsReadNotif(state, action: PayloadAction<number[]>) {
      state.myNotificationsState.notifications.map((notifs) => {
        if (action.payload.includes(notifs.notify_id)) {
          notifs.isNew = false;
        }
      });
    },

    // pending-tasks-abm reducers
    refreshPendingTasksABM: (
      state,
      action: PayloadAction<TaskAssignedByMe[]>,
    ) => {
      const newTasks = action.payload;
      state.pendingTasksABMState.pendingTasks = newTasks;
      state.pendingTasksABMState.pg = 2;
      if (convertNumber(newTasks?.length) >= 10) {
        state.pendingTasksABMState.showLoadMore = true;
      } else {
        state.pendingTasksABMState.showLoadMore = false;
      }
    },
    setPendingTasksABM: (state, action: PayloadAction<TaskAssignedByMe[]>) => {
      const newTasks = action.payload;
      const previousTasks = state.pendingTasksABMState.pendingTasks.map(
        (task) => ({
          ...task,
          isNew: false,
        }),
      );
      const previousTasksIds = previousTasks.map((task) => task.task_id);

      const newTasksFiltered = newTasks
        // filtering tasks present in previous task
        .filter((task) => !previousTasksIds.includes(task.task_id))
        // adding isNew key
        .map((task) => ({ ...task, isNew: true }));

      const allTasks = [...previousTasks, ...newTasksFiltered];

      state.pendingTasksABMState.pendingTasks = allTasks;
      state.pendingTasksABMState.pg += 1;

      if (convertNumber(newTasks?.length) < 10) {
        state.pendingTasksABMState.showLoadMore = false;
      }
    },

    updatePendingTasksABM: (
      state,
      action: PayloadAction<TaskAssignedByMe[]>,
    ) => {
      const newTasks = (action.payload || []).map((task) => ({
        ...task,
        isNew: true,
      }));
      const oldTasks = state.pendingTasksABMState.pendingTasks;
      const oldTaskIds = oldTasks.map((task) => task.task_id);
      const filteredNewTasks = newTasks.filter(
        (task) => !oldTaskIds.includes(task.task_id),
      );
      const allTasks = [...filteredNewTasks, ...oldTasks];
      state.pendingTasksABMState.pendingTasks = allTasks;
    },

    // completed-tasks-abm reducers
    refreshCompletedTasksABM: (
      state,
      action: PayloadAction<TaskAssignedByMe[]>,
    ) => {
      const newTasks = action.payload;
      state.completedTasksABMState.completedTasks = newTasks;
      state.completedTasksABMState.pg = 2;
      if (convertNumber(newTasks?.length) >= 10) {
        state.completedTasksABMState.showLoadMore = true;
      } else {
        state.completedTasksABMState.showLoadMore = false;
      }
    },
    setCompletedTasksABM: (
      state,
      action: PayloadAction<TaskAssignedByMe[]>,
    ) => {
      const newTasks = action.payload;
      const previousTasks = state.completedTasksABMState.completedTasks.map(
        (task) => ({
          ...task,
          isNew: false,
        }),
      );
      const previousTasksIds = previousTasks.map((task) => task.task_id);

      const newTasksFiltered = newTasks
        // filtering tasks present in previous task
        .filter((task) => !previousTasksIds.includes(task.task_id))
        // adding isNew key
        .map((task) => ({ ...task, isNew: true }));

      const allTasks = [...previousTasks, ...newTasksFiltered];

      state.completedTasksABMState.completedTasks = allTasks;
      state.completedTasksABMState.pg += 1;

      if (convertNumber(newTasks?.length) < 10) {
        state.pendingTasksState.showLoadMore = false;
      }
    },
    updateCompletedTasksABM: (
      state,
      action: PayloadAction<TaskAssignedByMe[]>,
    ) => {
      const newTasks = (action.payload || []).map((task) => ({
        ...task,
        isNew: true,
      }));
      const oldTasks = state.completedTasksABMState.completedTasks;
      const oldTaskIds = oldTasks.map((task) => task.task_id);
      const filteredNewTasks = newTasks.filter(
        (task) => !oldTaskIds.includes(task.task_id),
      );
      const allTasks = [...filteredNewTasks, ...oldTasks];
      state.completedTasksABMState.completedTasks = allTasks;
    },
    deleteTask: (state, action: PayloadAction<number>) => {
      state.pendingTasksABMState.pendingTasks =
        state.pendingTasksABMState.pendingTasks.filter(
          (task) => task.task_id !== action.payload,
        );
      state.completedTasksABMState.completedTasks =
        state.completedTasksABMState.completedTasks.filter(
          (task) => task.task_id !== action.payload,
        );
    },

    // my-notifications
    setMyNotifications: (state, action: PayloadAction<MyNotification[]>) => {
      const newNotifs = action.payload;
      const previousNotifs = state.myNotificationsState.notifications.map(
        (task) => ({
          ...task,
          isNew: false,
        }),
      );
      const previousNotifIds = previousNotifs.map((notif) => notif.notify_id);

      const newTasksFiltered = newNotifs
        // filtering tasks present in previous task
        .filter((task) => !previousNotifIds.includes(task.notify_id))
        // adding isNew key
        .map((task) => ({ ...task, isNew: true }));

      const allTasks = [...previousNotifs, ...newTasksFiltered];

      state.myNotificationsState.notifications = allTasks;
      state.myNotificationsState.pg += 1;
      state.myNotificationsState.notificationThisWeek = allTasks.filter(
        (task) => isThisWeek(new Date(task.updated_on)),
      ).length;

      if (convertNumber(newNotifs?.length) < 10) {
        state.myNotificationsState.showLoadMore = false;
      }
    },
    refreshNotifications: (state, action: PayloadAction<MyNotification[]>) => {
      const newNotifications = action.payload;
      state.myNotificationsState.notifications = newNotifications;
      state.myNotificationsState.pg = 2;
      state.myNotificationsState.notificationThisWeek = newNotifications.filter(
        (task) => isThisWeek(new Date(task.updated_on)),
      ).length;
      if (convertNumber(newNotifications?.length) >= 10) {
        state.myNotificationsState.showLoadMore = true;
      } else {
        state.myNotificationsState.showLoadMore = false;
      }
    },
    updateNotifications: (state, action: PayloadAction<MyNotification[]>) => {
      const newTasks = (action.payload || []).map((task) => ({
        ...task,
        isNew: true,
      }));
      const oldNotifs = state.myNotificationsState.notifications;
      const oldNotifIds = oldNotifs.map((task) => task.notify_id);
      const filteredNewTasks = newTasks.filter(
        (task) => !oldNotifIds.includes(task.notify_id),
      );
      const allTasks = [...filteredNewTasks, ...oldNotifs];
      state.myNotificationsState.notifications = allTasks;

      state.myNotificationsState.notificationThisWeek = allTasks.filter(
        (task) => isThisWeek(new Date(task.updated_on)),
      ).length;
    },

    // my-alarms
    setMyAlarms: (state, action: PayloadAction<MyAlarm[]>) => {
      const newAlarms = action.payload;
      const previousAlarms = state.myAlarmsState.alarms.map((alarm) => ({
        ...alarm,
        isNew: false,
      }));
      const previousNotifIds = previousAlarms.map((alarm) => alarm.alarm_id);

      const newTasksFiltered = newAlarms
        // filtering tasks present in previous task
        .filter((alarm) => !previousNotifIds.includes(alarm.alarm_id))
        // adding isNew key
        .map((alarm) => ({ ...alarm, isNew: true }));

      const allTasks = [...previousAlarms, ...newTasksFiltered];

      state.myAlarmsState.alarms = allTasks;
      state.myAlarmsState.pg += 1;
      state.myAlarmsState.todaysAlarmsCount = allTasks.filter((alarm) =>
        isToday(new Date(alarm.updated_on)),
      ).length;

      if (convertNumber(newAlarms?.length) < 10) {
        state.myAlarmsState.showLoadMore = false;
      }
    },
    refreshAlarms: (state, action: PayloadAction<MyAlarm[]>) => {
      const newAlarms = action.payload;
      state.myAlarmsState.alarms = newAlarms;
      state.myAlarmsState.pg = 2;
      state.myAlarmsState.todaysAlarmsCount = newAlarms.filter((task) =>
        isToday(new Date(task.updated_on)),
      ).length;
      if (convertNumber(newAlarms?.length) >= 10) {
        state.myAlarmsState.showLoadMore = true;
      } else {
        state.myAlarmsState.showLoadMore = false;
      }
    },
    updateAlarms: (state, action: PayloadAction<MyAlarm[]>) => {
      const newAlarms = (action.payload || []).map((task) => ({
        ...task,
        isNew: true,
      }));
      const oldAlarms = state.myAlarmsState.alarms;
      const oldAlarmIds = oldAlarms.map((task) => task.alarm_id);
      const filteredNewAlarms = newAlarms.filter(
        (task) => !oldAlarmIds.includes(task.alarm_id),
      );
      const allAlarms = [...filteredNewAlarms, ...oldAlarms];
      state.myAlarmsState.alarms = allAlarms;

      state.myAlarmsState.todaysAlarmsCount = allAlarms.filter((task) =>
        isToday(new Date(task.updated_on)),
      ).length;
    },
    setAsReadAlarms(state, action: PayloadAction<number[]>) {
      state.myAlarmsState.alarms.map((alarm) => {
        if (action.payload.includes(alarm.alarm_id)) {
          alarm.isNew = false;
        }
      });
    },

    resetStaffDashboardSlice: () => staffDMyTaskInitialState,
  },
});

export const staffDActions = staffDashboardSlice.actions;

export const staffDReducers = staffDashboardSlice.reducer;
